import { Box, Group, Skeleton, Stack, Text, Title, Tooltip, UnstyledButton } from "@mantine/core";
import { IconStar, IconStarFilled } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

// ─── Design tokens ──────────────────────────────────────────────────────────

export const T = {
  border: "#E5E7EB",
  borderStrong: "#D1D5DB",
  bgSubtle: "#F9FAFB",
  textPrimary: "#111827",
  textMuted: "#6B7280",
  textFaint: "#9CA3AF",
  primary: "#4F46E5",
  primarySoft: "rgba(79,70,229,0.08)",
  success: "#16A34A",
  warning: "#D97706",
  danger: "#DC2626",
  radius: 8,
};

export const PANEL_STYLE: React.CSSProperties = {
  background: "white",
  border: `1px solid ${T.border}`,
  borderRadius: T.radius,
};

// ─── Helpers ───────────────────────────────────────────────────────────────

export function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function pct(num: number, den: number): number {
  if (!den) return 0;
  return Math.round((num / den) * 100);
}

// ─── Date formatting ──────────────────────────────────────────────────────
// Standard short form everywhere: "May 3" for current year, "May 3, 2025"
// for prior years. Always pass to title=… for full date hover.

export function fmtDate(input: string | Date | null | undefined): string {
  if (!input) return "—";
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return "—";
  const now = new Date();
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function fmtDateTime(input: string | Date | null | undefined): string {
  if (!input) return "—";
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function fmtDateFull(input: string | Date | null | undefined): string {
  if (!input) return "—";
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export function fmtRelative(input: string | Date | null | undefined): string {
  if (!input) return "—";
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return "—";
  const diff = Date.now() - d.getTime();
  const day = 24 * 60 * 60 * 1000;
  if (diff < 60_000) return "just now";
  if (diff < 60 * 60_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < day) return `${Math.round(diff / (60 * 60_000))}h ago`;
  if (diff < 7 * day) return `${Math.round(diff / day)}d ago`;
  return fmtDate(d);
}

export function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

// ─── Avatar (initial circle) ──────────────────────────────────────────────

export function Avatar({ name, size = 22, color }: { name: string | null; size?: number; color?: string }) {
  // Deterministic color from name hash if not provided
  const hash = (name || "?").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const palette = ["#4F46E5", "#16A34A", "#D97706", "#0EA5E9", "#A855F7", "#DC2626", "#EC4899"];
  const bg = color || palette[hash % palette.length];
  return (
    <Box
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        borderRadius: size,
        background: bg,
        color: "white",
        fontSize: Math.round(size * 0.42),
        fontWeight: 600,
        flexShrink: 0,
        letterSpacing: "-0.02em",
      }}
    >
      {initials(name)}
    </Box>
  );
}

// ─── Empty / error states ──────────────────────────────────────────────────

export function EmptyState({
  title, hint, action,
}: {
  title: string;
  hint?: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <Box p="xl" style={PANEL_STYLE}>
      <Stack gap={6} align="center">
        <Text size="sm" c={T.textPrimary} fw={500}>{title}</Text>
        {hint && <Text size="xs" c={T.textMuted} ta="center">{hint}</Text>}
        {action && (
          <UnstyledButton
            onClick={action.onClick}
            mt={6}
            px={10}
            py={5}
            style={{
              borderRadius: 6,
              border: `1px solid ${T.border}`,
              color: T.primary,
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {action.label}
          </UnstyledButton>
        )}
      </Stack>
    </Box>
  );
}

export function FetchError({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  return (
    <Box
      p="md"
      style={{ ...PANEL_STYLE, borderColor: "rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.04)" }}
    >
      <Group gap={8} justify="space-between" align="center">
        <Text size="sm" c={T.danger}>
          {message || "Couldn't load — something went wrong."}
        </Text>
        {onRetry && (
          <UnstyledButton
            onClick={onRetry}
            px={10}
            py={4}
            style={{
              borderRadius: 6,
              border: `1px solid ${T.danger}`,
              color: T.danger,
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            Retry
          </UnstyledButton>
        )}
      </Group>
    </Box>
  );
}

// ─── Stale-while-revalidate hook ──────────────────────────────────────────
// Holds previous data during a refetch so the UI doesn't flash empty. Sets
// `isStale` true while a new fetch is in flight after the first load.

export function useSWRLike<T>(fetcher: () => Promise<T>, deps: any[]): {
  data: T | null;
  isLoading: boolean;
  isStale: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStale, setIsStale] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const tick = useRef(0);

  const run = useCallback(() => {
    const my = ++tick.current;
    setError(null);
    if (data !== null) setIsStale(true);
    else setIsLoading(true);
    fetcher()
      .then((r) => {
        if (my !== tick.current) return;
        setData(r);
        setError(null);
      })
      .catch((e: Error) => {
        if (my !== tick.current) return;
        setError(e);
      })
      .finally(() => {
        if (my !== tick.current) return;
        setIsLoading(false);
        setIsStale(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { run(); }, [run]);
  return { data, isLoading, isStale, error, refetch: run };
}

// ─── Stat card ─────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  accent?: string;
}

export function StatCard({ label, value, hint, accent }: StatCardProps) {
  return (
    <Box
      p="md"
      style={{ ...PANEL_STYLE, position: "relative", overflow: "hidden" }}
    >
      {accent && (
        <Box style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: accent }} />
      )}
      <Text size="11px" fw={700} c={T.textMuted} tt="uppercase" style={{ letterSpacing: "0.04em" }}>
        {label}
      </Text>
      <Text
        size="22px"
        fw={600}
        mt={6}
        c={T.textPrimary}
        style={{ lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}
      >
        {value}
      </Text>
      {hint && <Box mt={6}>{hint}</Box>}
    </Box>
  );
}

// ─── Page header ───────────────────────────────────────────────────────────

interface PageTitleProps {
  title: string;
  subtitle?: string;
  right?: ReactNode;
}

export function PageTitle({ title, subtitle, right }: PageTitleProps) {
  return (
    <Group justify="space-between" align="flex-end" pb="xs">
      <Group gap={6} align="baseline">
        <Title order={3} c={T.textPrimary} fw={600} style={{ letterSpacing: "-0.01em" }}>{title}</Title>
        {subtitle && (
          <>
            <Text c={T.textFaint}>·</Text>
            <Text size="sm" c={T.textMuted} fw={500}>{subtitle}</Text>
          </>
        )}
      </Group>
      {right}
    </Group>
  );
}

// ─── Signed pill ───────────────────────────────────────────────────────────

export function SignedPill({ status, method }: { status: string; method: string | null }) {
  if (status === "signed") {
    return (
      <Box
        component="span"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          height: 22,
          padding: "0 8px",
          borderRadius: 6,
          background: "rgba(22,163,74,0.1)",
          color: T.success,
          fontSize: 11,
          fontWeight: 600,
        }}
      >
        <Box w={6} h={6} style={{ borderRadius: 3, background: T.success }} />
        {method === "paper_photo" ? "Paper signed" : method === "live" ? "Online signed" : "Signed"}
      </Box>
    );
  }
  return (
    <Box
      component="span"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        height: 22,
        padding: "0 8px",
        borderRadius: 6,
        background: T.bgSubtle,
        color: T.textMuted,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      <Box w={6} h={6} style={{ borderRadius: 3, background: T.textFaint }} />
      Unsigned
    </Box>
  );
}

// ─── Segmented period picker ───────────────────────────────────────────────

export function PeriodPicker<V extends string>({
  value,
  onChange,
  options,
}: {
  value: V;
  onChange: (v: V) => void;
  options: { label: string; value: V }[];
}) {
  return (
    <Box
      style={{
        display: "inline-flex",
        height: 32,
        padding: 2,
        borderRadius: 8,
        border: `1px solid ${T.border}`,
        background: T.bgSubtle,
      }}
    >
      {options.map((o) => {
        const active = value === o.value;
        return (
          <Box
            key={o.value}
            onClick={() => onChange(o.value)}
            style={{
              padding: "0 12px",
              display: "inline-flex",
              alignItems: "center",
              borderRadius: 6,
              fontSize: 12,
              fontWeight: active ? 600 : 500,
              color: active ? T.textPrimary : T.textMuted,
              background: active ? "white" : "transparent",
              boxShadow: active ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
              cursor: "pointer",
              transition: "color 120ms ease",
            }}
          >
            {o.label}
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Table header style (for Table.Th cells) ───────────────────────────────

export const TH_STYLE: React.CSSProperties = {
  color: T.textMuted,
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

export const TABLE_HEAD_BG: React.CSSProperties = { background: T.bgSubtle };

// ─── Scroll shadow hook ────────────────────────────────────────────────────
// Returns a boolean that flips true once the nearest scrollable ancestor has
// scrolled past 0. Drop the boolean into a CSS box-shadow on the sticky bar.

export function useScrollShadow(): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    // Find the nearest scrollable ancestor of the document (Mantine AppShell
    // main is overflow:auto). Listen on it.
    let target: HTMLElement | Window = window;
    const main = document.querySelector(".mantine-AppShell-main") as HTMLElement | null;
    if (main) {
      const inner = main.firstElementChild as HTMLElement | null;
      target = inner || main;
    }
    const get = () => (target instanceof Window ? window.scrollY : target.scrollTop);
    const onScroll = () => setScrolled(get() > 4);
    onScroll();
    target.addEventListener("scroll", onScroll, { passive: true } as any);
    return () => target.removeEventListener("scroll", onScroll as any);
  }, []);
  return scrolled;
}

// ─── Skeleton helpers ──────────────────────────────────────────────────────

export function SkeletonStatsStrip({ cols = 3 }: { cols?: number }) {
  return (
    <Box style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 12 }}>
      {Array.from({ length: cols }).map((_, i) => (
        <Box key={i} p="md" style={PANEL_STYLE}>
          <Skeleton h={10} w={70} mb={10} />
          <Skeleton h={26} w={100} mb={8} />
          <Skeleton h={8} w="80%" />
        </Box>
      ))}
    </Box>
  );
}

export function SkeletonTable({ rows = 8, cols = 6 }: { rows?: number; cols?: number }) {
  return (
    <Box style={{ ...PANEL_STYLE, overflow: "hidden", padding: 12 }}>
      <Stack gap={10}>
        <Group gap={12} wrap="nowrap">
          {Array.from({ length: cols }).map((_, i) => (
            <Skeleton key={i} h={10} style={{ flex: 1 }} />
          ))}
        </Group>
        {Array.from({ length: rows }).map((_, i) => (
          <Group key={i} gap={12} wrap="nowrap">
            {Array.from({ length: cols }).map((_, j) => (
              <Skeleton key={j} h={14} style={{ flex: 1 }} />
            ))}
          </Group>
        ))}
      </Stack>
    </Box>
  );
}

export function SkeletonPanel({ h = 180 }: { h?: number }) {
  return (
    <Box p="md" style={PANEL_STYLE}>
      <Skeleton h={10} w={120} mb="md" />
      <Skeleton h={h} />
    </Box>
  );
}

// ─── Review / engagement cells (shared by Conversations + Per Charger) ────────

// ★★★☆☆ — rounded to nearest whole star for the compact cell, with the numeric
// rating appended. Renders "—" when there's no rating.
export function Stars({ rating, size = 13 }: { rating: number | null; size?: number }) {
  if (rating == null) return <Text size="12px" c={T.textFaint}>—</Text>;
  const full = Math.round(rating);
  return (
    <Group gap={1} wrap="nowrap" align="center">
      {[1, 2, 3, 4, 5].map((i) =>
        i <= full ? (
          <IconStarFilled key={i} size={size} color="#f59e0b" />
        ) : (
          <IconStar key={i} size={size} color={T.textFaint} />
        ),
      )}
      <Text size="12px" c={T.textMuted} ml={4} style={{ fontVariantNumeric: "tabular-nums" }}>
        {rating.toFixed(1)}
      </Text>
    </Group>
  );
}

// A rate as a percentage with a "num of den" tooltip; "—" when den is 0.
export function RateCell({ num, den }: { num: number; den: number }) {
  if (!den) return <Text size="13px" c={T.textFaint}>—</Text>;
  return (
    <Tooltip label={`${num} of ${den}`} withArrow>
      <Text size="13px" fw={500} c={T.textPrimary} style={{ fontVariantNumeric: "tabular-nums" }}>
        {pct(num, den)}%
      </Text>
    </Tooltip>
  );
}

export function BoolDot({ on }: { on: boolean }) {
  return <Box style={{ width: 8, height: 8, borderRadius: 4, background: on ? T.success : T.border }} />;
}
